import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/apiClient";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  History,
  Search,
  Filter,
  User,
  Shield,
  Clock,
  ArrowRight,
  RefreshCw,
  FileText,
} from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";

const USER_TABLES = ["profiles", "user_roles", "users"];

const TABLE_LABELS: Record<string, string> = {
  profiles: "Perfil de Usuario",
  user_roles: "Rol de Usuario",
  users: "Usuario",
  work_orders: "Orden de Trabajo",
  process_records: "Registro de Proceso",
  manufacturing_processes: "Proceso de Manufactura",
  production_batches: "Lote de Producción",
  quality_inspections: "Inspección de Calidad",
  material_inventory: "Materiales",
  raw_materials: "Materia Prima",
  product_models: "Modelo de Producto",
  molds: "Molde",
  machines: "Máquina",
  customers: "Cliente",
  news: "Noticia",
  links: "Enlace",
  albums: "Álbum",
  tools: "Herramienta",
  nfc_captures: "Captura NFC",
  nfc_tags: "Etiqueta NFC",
  supervisor_kpis: "KPI Supervisor",
  dashboard_configs: "Configuración de Panel",
  process_pauses: "Pausa de Proceso",
  push_subscriptions: "Suscripción Push",
};

const TABLE_FILTER_OPTIONS = [
  { value: "users", label: "Usuarios" },
  { value: "profiles", label: "Perfiles" },
  { value: "user_roles", label: "Roles" },
  { value: "work_orders", label: "Ordenes de Trabajo" },
  { value: "process_records", label: "Registros de Proceso" },
  { value: "manufacturing_processes", label: "Procesos de Manufactura" },
  { value: "production_batches", label: "Lotes de Producción" },
  { value: "quality_inspections", label: "Inspecciones de Calidad" },
  { value: "material_inventory", label: "Materiales" },
  { value: "raw_materials", label: "Materias Primas" },
  { value: "product_models", label: "Modelos de Producto" },
  { value: "molds", label: "Moldes" },
  { value: "machines", label: "Máquinas" },
  { value: "customers", label: "Clientes" },
  { value: "news", label: "Noticias" },
  { value: "links", label: "Enlaces" },
  { value: "albums", label: "Álbumes" },
  { value: "tools", label: "Herramientas" },
  { value: "nfc_captures", label: "Capturas NFC" },
  { value: "nfc_tags", label: "Etiquetas NFC" },
  { value: "supervisor_kpis", label: "KPIs Supervisor" },
  { value: "dashboard_configs", label: "Configuraciones de Panel" },
  { value: "process_pauses", label: "Pausas de Proceso" },
  { value: "push_subscriptions", label: "Suscripciones Push" },
];

interface AuditLog {
  id: string;
  user_id: string | null;
  table_name: string;
  operation: string;
  record_id: string | null;
  old_data: Record<string, unknown> | null;
  new_data: Record<string, unknown> | null;
  changed_fields: string[] | null;
  created_at: string | null;
  changed_by_name?: string;
  affected_user_name?: string;
}

interface UserOption {
  id: string;
  full_name: string | null;
}

const buildProfileMap = (profiles: UserOption[]) =>
  profiles.reduce((acc, profile) => {
    acc[profile.id] = profile.full_name || "Sin nombre";
    return acc;
  }, {} as Record<string, string>);

const formatRecordId = (recordId: string | null | undefined) => {
  if (!recordId) return "-";
  return recordId.length > 10 ? `${recordId.slice(0, 8)}...` : recordId;
};

export function AuditLogView() {
  const [searchTerm, setSearchTerm] = useState("");
  const [operationFilter, setOperationFilter] = useState("all");
  const [tableFilter, setTableFilter] = useState("all");
  const [userFilter, setUserFilter] = useState("all");

  const { data: userOptions } = useQuery({
    queryKey: ["audit-log-users"],
    queryFn: async () => {
      const { data, error } = await api
        .from("profiles")
        .select("id, full_name")
        .order("full_name", { ascending: true });

      if (error) throw error;
      return data as UserOption[];
    },
  });

  const { data: auditLogs, isLoading, refetch } = useQuery({
    queryKey: ["audit-logs-view", userFilter],
    queryFn: async () => {
      let query = api
        .from("audit_logs")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(200);

      if (userFilter !== "all") {
        query = query.eq("user_id", userFilter);
      }

      const { data, error } = await query;
      if (error) throw error;

      const logs = (data || []) as AuditLog[];
      const userIds = new Set<string>();

      logs.forEach((log) => {
        if (log.user_id) {
          userIds.add(log.user_id);
        }
        if (USER_TABLES.includes(log.table_name) && log.record_id) {
          userIds.add(log.record_id);
        }
      });

      if (userIds.size === 0) {
        return logs;
      }

      const { data: profiles } = await api
        .from("profiles")
        .select("id, full_name")
        .in("id", Array.from(userIds));

      const profileMap = buildProfileMap((profiles || []) as UserOption[]);

      return logs.map((log) => ({
        ...log,
        changed_by_name: log.user_id ? profileMap[log.user_id] || "Sistema" : "Sistema",
        affected_user_name:
          USER_TABLES.includes(log.table_name) && log.record_id
            ? profileMap[log.record_id] || "-"
            : undefined,
      }));
    },
  });

  const filteredLogs = auditLogs?.filter((log) => {
    const searchValue = searchTerm.toLowerCase();
    const matchesSearch =
      searchTerm === "" ||
      log.changed_by_name?.toLowerCase().includes(searchValue) ||
      log.affected_user_name?.toLowerCase().includes(searchValue) ||
      log.record_id?.toLowerCase().includes(searchValue) ||
      log.table_name?.toLowerCase().includes(searchValue);

    const matchesOperation =
      operationFilter === "all" || log.operation === operationFilter;

    const matchesTable = tableFilter === "all" || log.table_name === tableFilter;

    return matchesSearch && matchesOperation && matchesTable;
  });

  const getOperationBadge = (operation: string) => {
    const config: Record<string, { label: string; color: string }> = {
      INSERT: { label: "Creado", color: "bg-green-500/10 text-green-500" },
      UPDATE: { label: "Actualizado", color: "bg-blue-500/10 text-blue-500" },
      DELETE: { label: "Eliminado", color: "bg-red-500/10 text-red-500" },
    };
    const { label, color } = config[operation] || {
      label: operation,
      color: "bg-gray-500/10 text-gray-500",
    };
    return <Badge className={color}>{label}</Badge>;
  };

  const getTableLabel = (table: string) => TABLE_LABELS[table] || table;

  const formatChanges = (log: AuditLog) => {
    if (log.table_name === "user_roles") {
      if (log.operation === "UPDATE") {
        const oldRole = log.old_data?.role as string | undefined;
        const newRole = log.new_data?.role as string | undefined;
        return (
          <span className="text-sm">
            Rol: <span className="text-muted-foreground">{oldRole}</span>
            <ArrowRight className="w-3 h-3 inline mx-1" />
            <span className="font-medium">{newRole}</span>
          </span>
        );
      }
      const role = (log.new_data?.role || log.old_data?.role) as string | undefined;
      return (
        <span className="text-sm">
          Rol: <span className="font-medium">{role}</span>
        </span>
      );
    }

    if (log.table_name === "profiles" && log.changed_fields) {
      const relevantFields = log.changed_fields.filter((f) => f !== "updated_at");
      if (relevantFields.length === 0) {
        return <span className="text-sm text-muted-foreground">Sin cambios relevantes</span>;
      }

      if (relevantFields.includes("is_active")) {
        const oldActive = log.old_data?.is_active as boolean | undefined;
        const newActive = log.new_data?.is_active as boolean | undefined;
        return (
          <span className="text-sm">
            Estado:{" "}
            <span className="text-muted-foreground">
              {oldActive ? "Activo" : "Inactivo"}
            </span>
            <ArrowRight className="w-3 h-3 inline mx-1" />
            <span className="font-medium">
              {newActive ? "Activo" : "Inactivo"}
            </span>
          </span>
        );
      }

      return (
        <span className="text-sm text-muted-foreground">
          Campos: {relevantFields.join(", ")}
        </span>
      );
    }

    if (log.changed_fields && log.changed_fields.length > 0) {
      const relevantFields = log.changed_fields.filter(
        (field) => !["updated_at", "created_at"].includes(field)
      );
      if (relevantFields.length === 0) {
        return <span className="text-sm text-muted-foreground">-</span>;
      }
      return (
        <span className="text-sm text-muted-foreground">
          Campos: {relevantFields.slice(0, 4).join(", ")}
          {relevantFields.length > 4 ? ` +${relevantFields.length - 4} más` : ""}
        </span>
      );
    }

    return <span className="text-sm text-muted-foreground">-</span>;
  };

  return (
    <div className="space-y-6">
      {/* Header y filtros */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por usuario, tabla o registro..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>

        <Select value={userFilter} onValueChange={setUserFilter}>
          <SelectTrigger className="w-[220px]">
            <User className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Usuario" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los usuarios</SelectItem>
            {(userOptions || []).map((userOption) => (
              <SelectItem key={userOption.id} value={userOption.id}>
                {userOption.full_name
                  ? userOption.full_name
                  : `Sin nombre (${userOption.id.slice(0, 8)})`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={operationFilter} onValueChange={setOperationFilter}>
          <SelectTrigger className="w-[150px]">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Operación" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas</SelectItem>
            <SelectItem value="INSERT">Creados</SelectItem>
            <SelectItem value="UPDATE">Actualizados</SelectItem>
            <SelectItem value="DELETE">Eliminados</SelectItem>
          </SelectContent>
        </Select>

        <Select value={tableFilter} onValueChange={setTableFilter}>
          <SelectTrigger className="w-[180px]">
            <FileText className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Tabla" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas</SelectItem>
            {TABLE_FILTER_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button variant="outline" size="icon" onClick={() => refetch()}>
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <History className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{auditLogs?.length || 0}</p>
                <p className="text-sm text-muted-foreground">Total Registros</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-500/10">
                <User className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {auditLogs?.filter((l) => l.operation === "INSERT").length || 0}
                </p>
                <p className="text-sm text-muted-foreground">Creaciones</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <Shield className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {auditLogs?.filter((l) => l.operation === "UPDATE").length || 0}
                </p>
                <p className="text-sm text-muted-foreground">Actualizaciones</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-500/10">
                <Clock className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {auditLogs?.filter((l) => l.operation === "DELETE").length || 0}
                </p>
                <p className="text-sm text-muted-foreground">Eliminaciones</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabla de logs */}
      {isLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
        </div>
      ) : filteredLogs && filteredLogs.length > 0 ? (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-4 font-medium w-[180px]">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        Fecha
                      </div>
                    </th>
                    <th className="text-left p-4 font-medium">Modificado por</th>
                    <th className="text-left p-4 font-medium">Registro</th>
                    <th className="text-left p-4 font-medium">Tabla</th>
                    <th className="text-left p-4 font-medium">Acción</th>
                    <th className="text-left p-4 font-medium">Cambios</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLogs.map((log) => (
                    <tr key={log.id} className="border-b hover:bg-muted/30">
                      <td className="p-4 text-sm">
                        {log.created_at
                          ? format(new Date(log.created_at), "dd/MM/yyyy HH:mm:ss", {
                              locale: es,
                            })
                          : "-"}
                      </td>
                      <td className="p-4">
                        <span className="font-medium">{log.changed_by_name || "Sistema"}</span>
                      </td>
                      <td className="p-4">
                        {log.affected_user_name ? (
                          <span className="font-medium">{log.affected_user_name}</span>
                        ) : log.record_id ? (
                          <span className="text-xs text-muted-foreground" title={log.record_id}>
                            {formatRecordId(log.record_id)}
                          </span>
                        ) : (
                          "-"
                        )}
                      </td>
                      <td className="p-4">
                        <Badge variant="outline">{getTableLabel(log.table_name)}</Badge>
                      </td>
                      <td className="p-4">{getOperationBadge(log.operation)}</td>
                      <td className="p-4 max-w-xs">{formatChanges(log)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <History className="w-12 h-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">
              {searchTerm || operationFilter !== "all" || tableFilter !== "all" || userFilter !== "all"
                ? "No se encontraron registros con los filtros seleccionados"
                : "No hay registros de auditoría"}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
